import React, { useState, useEffect } from 'react';
import type { Table, TableShape } from '../../types';

interface TableModalProps {
  table: Table | null;
  onClose: () => void;
  onSave: (table: Omit<Table, 'status'>) => void;
  onDelete?: (tableId: number) => void;
}

const TableModal: React.FC<TableModalProps> = ({ table, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    name: '',
    capacity: 2,
    shape: 'square' as TableShape,
  });
  const [id, setId] = useState<number | null>(null);

  useEffect(() => {
    if (table) {
      setFormData({
        name: table.name,
        capacity: table.capacity,
        shape: table.shape || 'square',
      });
      setId(table.id);
    } else {
        setFormData({ name: '', capacity: 2, shape: 'square' });
        setId(null);
    }
  }, [table]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'capacity' ? parseInt(value, 10) : value }));
  };

  const handleShapeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, shape: e.target.value as TableShape }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || formData.capacity <= 0) {
        alert('Please provide a valid name and capacity.');
        return;
    }
    onSave({ ...formData, id: id || 0 });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">{table ? 'Edit Table' : 'Add New Table'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Table Name</label>
            <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} placeholder="e.g., Table 15 or Patio 4" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500" required />
          </div>
          <div className="mb-4">
            <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-1">Capacity (Seats)</label>
            <input type="number" name="capacity" id="capacity" value={formData.capacity} onChange={handleChange} min="1" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500" required />
          </div>
          <div className="mb-6">
            <label htmlFor="shape" className="block text-sm font-medium text-gray-700 mb-1">Table Shape</label>
            <select
              id="shape"
              name="shape"
              value={formData.shape}
              onChange={handleShapeChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="square">Rectangle</option>
              <option value="round">Round</option>
            </select>
          </div>
          <div className="flex justify-between items-center">
            <div>
              {table && onDelete && (
                <button 
                    type="button" 
                    onClick={() => onDelete(table.id)} 
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 font-semibold"
                >
                    Delete Table
                </button>
              )}
            </div>
            <div className="flex justify-end space-x-4">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600">Save Table</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TableModal;